import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { showApiError, showSuccess } from "@/lib/toast";
import { addAreaContent, deleteAreaContent, updateArea } from "@/sideEffects/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AreaMetaForm, FormSchemaType } from "./form/AreaMetaForm";

interface Props {
  onSuccess: () => void;
  onOpenChange: (open: boolean) => void;
  motherAreaintId?: number;
  idString: string;
  idNumber: number;
  defaultValues: FormSchemaType;
}

export const UpdateAreaSheet: React.FC<Props> = ({
  onSuccess,
  onOpenChange,
  idString,
  idNumber,
  motherAreaintId,
  defaultValues,
}) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: async (data: FormSchemaType) => {
      await updateArea({
        id: idString,
        firmGuid: String(activeFirm?.firmGuid),
        name: data.name,
        timezone: data.timezone,
        motherAreaintId: motherAreaintId,
        defaultContentId: data.defaultContentId,
        areaTypeId: data.areaTypeId,
        overwriteSub: data.overwriteSub,
      });

      console.log(defaultValues.contentList);

      for (const existing of defaultValues.contentList || []) {
        await deleteAreaContent(existing.id || "", String(activeFirm?.firmGuid));
      }

      if (data.contentList) {
        for (const content of data.contentList) {
          if (content.contentId || content.contentGroupId) {
            await addAreaContent({
              areaId: idNumber,
              contentId: content.contentId || undefined,
              contentGroupId: content.contentGroupId || undefined,
              firmGuid: String(activeFirm?.firmGuid),
            });
          }
        }
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["area"] });
      queryClient.invalidateQueries({ queryKey: ["getAreaCampaign"] });
      queryClient.invalidateQueries({ queryKey: ["getAreaContent"] });
      onOpenChange(false);
      onSuccess();
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const onSubmit = (data: FormSchemaType) => {
    updateMutation.mutate(data);
  };

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto w-full sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{t("common.entityDetail", { name: defaultValues.name })}</SheetTitle>
          <AreaMetaForm
            buttonText={t("common.save")}
            onSubmit={onSubmit}
            isLoading={updateMutation.isPending}
            defaultValues={defaultValues}
          />
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
